import {
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  Box,
  Typography,
  Paper,
  Tooltip,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../../../styles/theme';

export const ModernTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(0, 0, 2, 0),
}));

export const ModernPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  ...(theme.palette.mode === 'dark'
    ? {
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
      }
    : {
        boxShadow: '0 4px 32px 0 rgba(16,30,54,0.08)',
      }),
  padding: theme.spacing(0, 0, 2, 0),
}));

export const ModernTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: 0.8,
    textTransform: 'capitalize',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'transparent',
    paddingTop: theme.spacing(0.8),
    paddingBottom: theme.spacing(0.8),
    paddingLeft: 0, // Sin padding horizontal - todo a 64px
    paddingRight: 0, // Sin padding horizontal - todo a 64px
  },
}));

export const ModernTableRow = styled(TableRow)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: 'background 0.2s',
  '& .MuiTableCell-root': {
    borderBottom: 'none',
    fontSize: '1rem',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: 0, // Sin padding horizontal - todo a 64px
    paddingRight: 0, // Sin padding horizontal - todo a 64px
    background: 'transparent',
  },
}));

export const StatusChip = styled(Chip)<{ color: 'success' | 'error' }>(({ theme, color }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  borderRadius: theme.spacing(2.5),
  paddingX: theme.spacing(1.5),
  height: 26,
  backgroundColor: color === 'success' ? `${colors.success.light}20` : `${colors.error.light}20`,
  color: color === 'success' ? colors.success.dark : colors.error.dark,
  letterSpacing: 0.5,
  minWidth: 75,
  textAlign: 'center',
  border: 'none',
  '& .MuiChip-label': {
    paddingX: theme.spacing(2),
  },
}));

export interface ActionIconButtonProps {
  color?: 'primary' | 'info' | 'error';
}

export const ActionIconButton = styled(IconButton)<ActionIconButtonProps>(({ theme, color }) => {
  let paletteColor = theme.palette.primary;
  if (color === 'info') paletteColor = theme.palette.info;
  if (color === 'error') paletteColor = theme.palette.error;
  if (color === 'primary') paletteColor = theme.palette.primary;
  return {
    padding: '4px',
    color: paletteColor.main,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: alpha(paletteColor.main, 0.2),
      boxShadow: 'none',
    },
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  };
});

export const ProductName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.05rem',
  letterSpacing: 0.2,
}));

export const ProductCode = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.98rem',
  letterSpacing: 0.5,
}));

export const PriceText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#16a34a',
  fontSize: '1.05rem',
}));

export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
}));

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600],
  '&.Mui-checked': {
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.grey[600],
  },
  '&.MuiCheckbox-indeterminate': {
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.grey[600],
  },
}));

export const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  // Tooltip styles can be added here if needed
}));
